import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ParentAiService } from './parent-ai.service';
import { getCategoryLabelKey } from './constants/topic-category.constants';
import { UserService } from '../user/service/user.service';
import { getValvurApiError } from '../../core/errors/parse-valvur-api-error';
import { FamilyKidsService } from '../parent-family/services/family-kids.service';
import { useBreakpoint } from '../../shared/composables/useBreakpoint';
import { ai as AiGen } from '@valvur/shared-grpc';
import { RealtimeEvents, Scribe } from '@elevenlabs/client';
import type { ChildOption } from './models/child-option.model';
import type { DisplayMessage } from './models/display-message.model';
import type { ParentAiQuickAction } from './models/quick-action.model';
import { ParentAiVerificationBannerComponent } from './components/parent-ai-verification-banner/parent-ai-verification-banner.component';
import { ParentAiPlanGateBannerComponent } from './components/parent-ai-plan-gate-banner/parent-ai-plan-gate-banner.component';
import { ParentAiAssistantCardComponent } from './components/parent-ai-assistant-card/parent-ai-assistant-card.component';
import { ParentAiTopicsPanelComponent } from './components/parent-ai-topics-panel/parent-ai-topics-panel.component';
import { ParentAiActiveTopicBarComponent } from './components/parent-ai-active-topic-bar/parent-ai-active-topic-bar.component';
import { ParentAiChatBodyComponent } from './components/parent-ai-chat-body/parent-ai-chat-body.component';
import { ParentAiChatFooterComponent } from './components/parent-ai-chat-footer/parent-ai-chat-footer.component';
import { ParentAiDisclaimerBannerComponent } from './components/parent-ai-disclaimer-banner/parent-ai-disclaimer-banner.component';
import { ParentAiOverlayComponent } from './components/parent-ai-overlay/parent-ai-overlay.component';

export type { ChildOption } from './models/child-option.model';
export type { DisplayMessage } from './models/display-message.model';

@Component({
  selector: 'app-parent-ai-assistant',
  standalone: true,
  imports: [
    ParentAiVerificationBannerComponent,
    ParentAiPlanGateBannerComponent,
    ParentAiAssistantCardComponent,
    ParentAiTopicsPanelComponent,
    ParentAiActiveTopicBarComponent,
    ParentAiChatBodyComponent,
    ParentAiChatFooterComponent,
    ParentAiDisclaimerBannerComponent,
    ParentAiOverlayComponent,
  ],
  providers: [],
  templateUrl: './parent-ai-assistant.component.html',
  styleUrl: './parent-ai-assistant.component.scss',
})
export class ParentAiAssistantComponent {
  @ViewChild(ParentAiChatBodyComponent) chatBody!: ParentAiChatBodyComponent;

  #parentAi = inject(ParentAiService);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #translate = inject(TranslateService);

  readonly currentBreakpoint = useBreakpoint().currentBreakpoint;
  /** On mobile (XSmall), layout shows ai-assistant-header at top; hide our duplicate header. */
  showHeader = computed(() => this.currentBreakpoint() !== 'XSmall');
  /** Speech-to-text on mobile when plan allows (or payments off). */
  showSpeechToText = computed(() => {
    const mobile = this.currentBreakpoint() === 'XSmall' || this.isMobile();
    if (!mobile) {
      return false;
    }
    return this.#userService.speechToTextAllowed();
  });

  isMobile = input<boolean>(false);
  embedded = input<boolean>(true);

  showSidebar = signal(false);
  showTopicsPanel = signal(false);
  messageInput = signal('');
  /** When true, chat area is cleared until user sends a message (Start new conversation). */
  showNewConversation = signal(false);
  /** Polite error message when send fails (assistant-style). */
  sendErrorMessage = signal<string | null>(null);
  /** Optimistic user message shown immediately on send until server state includes it. */
  pendingUserMessage = signal<{ content: string } | null>(null);
  /** True while fetching scribe token (before recording starts). */
  isTranscribing = signal(false);
  /** True when Scribe is connected and ready; mic is active, user can speak. */
  isRecording = signal(false);

  // ─── User & children from API ─────────────────────────────────────────────

  user = computed(() => this.#userService.user());
  children = computed<ChildOption[]>(() =>
    this.#familyKids.toChildOptions(
      this.#translate.instant('app.parentPatrol.labels.childFallback'),
    ),
  );
  linkedKidIds = computed(() => this.children().map((c) => c.id));
  selectedChildId = this.#parentAi.selectedChildId;
  selectedChild = computed(() => {
    const id = this.selectedChildId();
    if (!id) return null;
    return this.children().find((c) => c.id === id) ?? null;
  });
  /** True only when user has loaded and has no family children. */
  isVerificationMode = computed(() => {
    if (this.user() == null) return false;
    return !this.#familyKids.hasChildren();
  });
  /** Show info tip whenever AI has no linked children (not a warning). */
  showVerificationBanner = computed(() => this.isVerificationMode());
  /** Plan/quota gate from last send (soft feature gate or hard quota). */
  planGateCode = computed(() => this.#parentAi.planGateCode());
  planGateLimit = computed(() => this.#parentAi.planGateLimit());
  /**
   * Kid-only chrome (selector, topics, quick chips): hidden until user is loaded and at least one child exists.
   * Avoids flicker where `isVerificationMode` is false while `user` is still null.
   */
  showKidScopedUi = computed(() => {
    if (this.user() == null) return false;
    return this.#familyKids.hasChildren();
  });
  userDisplayName = computed(() => {
    const u =
      (this.user()?.personalData as {
        firstName?: string;
        lastName?: string;
        name?: string;
      } | null) ?? null;
    if (!u) return 'You';
    return u.firstName ?? u.lastName ?? u.name ?? 'You';
  });

  userChatAvatarUrl = computed(() => {
    const pd = this.user()?.personalData as { avatar?: string } | undefined;
    const url = pd?.avatar?.trim();
    return url || null;
  });

  userChatInitialsSource = computed(() => {
    const pd = this.user()?.personalData as {
      firstName?: string;
      lastName?: string;
    } | null;
    return pd ?? { firstName: '', lastName: '' };
  });

  // ─── Messages (normalized for template) ───────────────────────────────────

  displayMessages = computed<DisplayMessage[]>(() => {
    const pending = this.pendingUserMessage();
    if (this.#parentAi.kidAwaitingTopicSync() || this.showNewConversation()) {
      return pending
        ? [
            {
              id: 'pending-user',
              role: 'user' as const,
              content: pending.content,
            },
          ]
        : [];
    }
    const raw = this.#parentAi.displayMessages();
    const fromServer = raw.map((m, i) => ({
      id: `msg-${i}-${m.role}`,
      role: m.role as 'user' | 'model',
      content: (m.parts ?? [])
        .map((p: { text?: string }) => p?.text ?? '')
        .join('\n'),
    }));
    if (pending) {
      return [
        ...fromServer,
        { id: 'pending-user', role: 'user', content: pending.content },
      ];
    }
    return fromServer;
  });

  // ─── Topics from API ──────────────────────────────────────────────────────

  topics = computed(() => this.#parentAi.topics());
  selectedTopicId = this.#parentAi.selectedTopicId;
  selectedTopic = computed(() => {
    const id = this.selectedTopicId();
    if (!id) return null;
    return this.topics().find((t) => t.id === id) ?? null;
  });
  filteredTopics = computed(() => this.topics());
  /** Kid-specific quick chips (duties, interests, patrol) need a selected child. */
  isNeutralChildScope = computed(() =>
    this.#parentAi.isNeutralParentingScope(),
  );
  topicCounts = computed(() => {
    const counts: Record<string, number> = {};
    for (const t of this.filteredTopics()) {
      const key = t.primaryTopic ?? 'general';
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  });

  // ─── Loading & error ──────────────────────────────────────────────────────

  isLoadingConversation = computed(
    () =>
      (this.isVerificationMode() &&
        this.#parentAi.conversationQuery.isPending()) ||
      (!!this.selectedTopicId() &&
        this.#parentAi.conversationForTopicQuery.isPending()),
  );
  isSending = computed(() => this.#parentAi.isSending());
  isLoadingTopics = computed(() => this.#parentAi.topicsQuery.isPending());

  // ─── Quick actions (fill input only) ──────────────────────────────────────

  quickActions = [
    {
      id: '1',
      labelKey: 'app.components.parentAiAssistant.quickActions.behavior',
      icon: '🧠',
      category: 'behavior_regulation',
    },
    {
      id: '2',
      labelKey: 'app.components.parentAiAssistant.quickActions.screenTime',
      icon: '📱',
      category: 'screen_time',
    },
    {
      id: '3',
      labelKey: 'app.components.parentAiAssistant.quickActions.homework',
      icon: '📚',
      category: 'homework_struggles',
    },
    {
      id: '4',
      labelKey: 'app.components.parentAiAssistant.quickActions.sleep',
      icon: '😴',
      category: 'sleep',
    },
    {
      id: '5',
      labelKey: 'app.components.parentAiAssistant.quickActions.anxiety',
      icon: '😰',
      category: 'anxiety_worry',
    },
    {
      id: '6',
      labelKey: 'app.components.parentAiAssistant.quickActions.friends',
      icon: '👥',
      category: 'friendships',
    },
    {
      id: '7',
      labelKey: 'app.components.parentAiAssistant.quickActions.duties',
      icon: '✅',
      category: 'duties',
      promptKey: 'app.components.parentAiAssistant.quickActionPromptDuties',
    },
    {
      id: '7b',
      labelKey: 'app.components.parentAiAssistant.quickActions.interests',
      icon: '🌟',
      category: 'interests',
      promptKey: 'app.components.parentAiAssistant.quickActionPromptInterests',
    },
    {
      id: '7c',
      labelKey: 'app.components.parentAiAssistant.quickActions.patrolSafety',
      icon: '🛡️',
      category: 'online_safety',
      promptKey:
        'app.components.parentAiAssistant.quickActionPromptPatrolSafety',
    },
    {
      id: '8',
      labelKey: 'app.components.parentAiAssistant.quickActions.courses',
      icon: '📚',
      category: 'courses',
      promptKey: 'app.components.parentAiAssistant.quickActionPromptCourses',
    },
  ] as const satisfies readonly ParentAiQuickAction[];

  constructor() {
    // Sync resolved verification mode to service (same logic as isVerificationMode computed)
    effect(() => {
      this.#parentAi.isVerificationMode.set(this.isVerificationMode());
    });
    effect(() => {
      if (this.#parentAi.preferNewConversation()) {
        this.showNewConversation.set(true);
        this.#parentAi.preferNewConversation.set(false);
      }
    });
    effect(() => {
      if (this.displayMessages().length > 0) {
        queueMicrotask(() => this.scrollToBottom(false));
      }
    });
    effect(() => {
      if (this.isSending()) {
        queueMicrotask(() => this.scrollToBottom(true));
      }
    });
    // Clear optimistic user message once server has the conversation (avoids duplicate / flash)
    effect(() => {
      if (
        !this.isSending() &&
        this.pendingUserMessage() &&
        this.#parentAi.displayMessages().length > 0
      ) {
        this.pendingUserMessage.set(null);
      }
    });
  }

  selectTopic(topic: AiGen.TopicThreadDto): void {
    this.#parentAi.selectedTopicId.set(topic.id);
    this.showNewConversation.set(false);
    this.showTopicsPanel.set(false);
  }

  startNewConversation(): void {
    this.#parentAi.startNewConversation();
    this.showTopicsPanel.set(false);
    this.showNewConversation.set(true);
    this.sendErrorMessage.set(null);
    this.pendingUserMessage.set(null);
  }

  dismissPlanGate(): void {
    this.#parentAi.dismissPlanGate();
  }

  toggleSidebar(): void {
    this.showSidebar.update((v) => !v);
  }

  toggleTopicsPanel(): void {
    if (!this.showKidScopedUi() || this.isNeutralChildScope()) return;
    this.showTopicsPanel.update((v) => !v);
  }

  sendMessage(): void {
    const message = this.messageInput().trim();
    if (!message) return;

    this.sendErrorMessage.set(null);
    const kidIdAtSend = this.selectedChildId();
    const topicIdAtSend = this.selectedTopicId();
    const { kidId: scopedKidId, topicId: scopedTopicId } =
      this.#parentAi.resolveSendScope(kidIdAtSend, topicIdAtSend);
    if (topicIdAtSend && !scopedTopicId) {
      this.#parentAi.selectedTopicId.set(null);
    }
    const linkedKidIds = this.linkedKidIds();
    const dto = {
      message,
      linkedKidIds,
      kidId: scopedKidId,
      topicId: scopedTopicId,
      detailedParentResponse: false, // TODO: add this back in when we have the feature
      country: this.user()?.personalData?.country?.trim() ?? '',
      language:
        this.#userService.settings()?.language?.trim() ||
        this.#translate.currentLang ||
        'en',
    } satisfies AiGen.MessageDto & { language: string };

    this.pendingUserMessage.set({ content: message });
    this.messageInput.set('');
    this.showNewConversation.set(false); // show message list immediately with optimistic message + typing

    this.#parentAi.sendMessageMutation
      .mutateAsync(dto)
      .then(async (conversation) => {
        if (this.isVerificationMode()) {
          this.pendingUserMessage.set(null);
          return;
        }
        await this.#parentAi.applyConversationAfterSend(
          conversation,
          kidIdAtSend,
          scopedTopicId || undefined,
        );
        this.#parentAi.refetchTopicsForChild(kidIdAtSend);
        this.pendingUserMessage.set(null);
      })
      .catch((error: unknown) => {
        this.pendingUserMessage.set(null);
        this.#parentAi.pendingThreadConversation.set(null);
        const apiError = getValvurApiError(error);
        const scopeKey = apiError?.meta?.scopeKey?.trim();
        if (
          apiError?.errorCode === 'PAYMENTS_QUOTA_EXCEEDED' &&
          scopeKey
        ) {
          this.#parentAi.setPlanGate(scopeKey, apiError.meta?.limit ?? null);
          this.sendErrorMessage.set(null);
          return;
        }
        this.sendErrorMessage.set(
          this.#translate.instant('app.components.parentAiAssistant.sendError'),
        );
      });
  }

  useQuickAction(action: ParentAiQuickAction): void {
    const kidScoped = ['duties', 'interests', 'online_safety'] as const;
    if (
      this.isNeutralChildScope() &&
      kidScoped.includes(action.category as (typeof kidScoped)[number])
    ) {
      this.messageInput.set(
        this.#translate.instant(
          'app.components.parentAiAssistant.quickActionRequiresChild',
        ),
      );
      return;
    }
    const name = this.selectedChild()?.name ?? 'my child';
    const promptKey =
      'promptKey' in action && action.promptKey
        ? action.promptKey
        : 'app.components.parentAiAssistant.quickActionPrompt';
    if (promptKey === 'app.components.parentAiAssistant.quickActionPrompt') {
      const topic = this.#translate.instant(
        getCategoryLabelKey(action.category),
      );
      this.messageInput.set(
        this.#translate.instant(promptKey, { topic, name }),
      );
    } else {
      this.messageInput.set(this.#translate.instant(promptKey, { name }));
    }
  }

  resolveTopic(topicId: string, notes?: string): void {
    this.#parentAi.resolveTopicMutation.mutate({
      topicId,
      resolutionNotes: notes ?? '',
    });
  }

  /** @param force Pin viewport to bottom (e.g. user just sent a message). */
  private scrollToBottom(force = false): void {
    this.chatBody?.scrollToBottom(false, force);
  }

  #scribeConnection: { close: () => void } | null = null;
  #accumulatedTranscript = '';
  #lastPartialTranscript = '';
  #recordingStartedAt: number | null = null;

  public async toggleSpeechToText(): Promise<void> {
    if (this.isRecording()) {
      this.stopScribe();
    } else {
      await this.startScribe();
    }
  }

  private async startScribe(): Promise<void> {
    try {
      this.sendErrorMessage.set(null);
      this.#accumulatedTranscript = '';
      this.#lastPartialTranscript = '';
      this.isTranscribing.set(true);

      const token = await this.#parentAi.scribeTokenMutation.mutateAsync();
      if (!token) {
        throw new Error('No token received');
      }

      const connection = Scribe.connect({
        token,
        modelId: 'scribe_v2_realtime',
        microphone: {},
      });

      connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, (data) => {
        if (data.text?.trim()) {
          this.#lastPartialTranscript = data.text;
        }
      });
      connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, (data) => {
        if (data.text?.trim()) {
          this.#accumulatedTranscript = this.#accumulatedTranscript
            ? `${this.#accumulatedTranscript} ${data.text.trim()}`
            : data.text.trim();
          this.#lastPartialTranscript = '';
        }
      });
      connection.on(RealtimeEvents.ERROR, (error) => {
        console.error('ElevenLabs Scribe Error:', error);
        this.sendErrorMessage.set(
          this.#translate.instant(
            'app.components.parentAiAssistant.speechToTextError',
          ),
        );
        this.stopScribe();
      });

      connection.on(RealtimeEvents.SESSION_STARTED, () => {
        this.isTranscribing.set(false);
        this.isRecording.set(true);
        this.#recordingStartedAt = Date.now();
      });

      this.#scribeConnection = connection;
    } catch (err: unknown) {
      this.isTranscribing.set(false);
      const apiError = getValvurApiError(err);
      const scopeKey = apiError?.meta?.scopeKey?.trim();
      if (
        apiError?.errorCode === 'PAYMENTS_QUOTA_EXCEEDED' &&
        scopeKey
      ) {
        this.#parentAi.setPlanGate(scopeKey, apiError.meta?.limit ?? null);
        this.sendErrorMessage.set(null);
        return;
      }
      this.sendErrorMessage.set(
        this.#translate.instant(
          'app.components.parentAiAssistant.speechToTextError',
        ),
      );
    }
  }

  private stopScribe(): void {
    const startedAt = this.#recordingStartedAt;
    this.#recordingStartedAt = null;

    if (this.#scribeConnection) {
      this.#scribeConnection.close();
      this.#scribeConnection = null;
    }
    this.isRecording.set(false);
    this.isTranscribing.set(false);

    if (startedAt != null) {
      const durationSeconds = Math.max(
        0,
        Math.round((Date.now() - startedAt) / 1000),
      );
      if (durationSeconds > 0) {
        void this.#parentAi.recordSpeechUsageMutation
          .mutateAsync(durationSeconds)
          .catch(() => {});
      }
    }

    const finalText = [this.#accumulatedTranscript, this.#lastPartialTranscript]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (finalText) {
      const current = this.messageInput();
      this.messageInput.set(current ? `${current} ${finalText}` : finalText);
    }
    this.#accumulatedTranscript = '';
    this.#lastPartialTranscript = '';
  }
}
